/**
 * ADServiceSitesEditor — Sedes (AD sites) documentadas de un servicio AD.
 *
 * El backend ya guardaba `ad_topology.sites` (nombre, subredes, descripción y
 * sede primaria) pero el frontend nunca lo enviaba, así que no se podía
 * documentar la topología de sitios. Este editor cubre ese hueco: permite
 * nombrar la sede, asociarla a una Location del CMDB, listar sus subredes
 * (una por coma) y marcar cuál es la primaria.
 *
 * Además permite FIJAR DCs a una sede (`pinned_dcs`): sin eso, un DC cuyo
 * campo «Sitio AD» no coincide con el nombre documentado de la sede aparecía
 * en una sede extra creada automáticamente.
 */
import React, { useMemo } from 'react';
import { PlusIcon, TrashIcon, StarIcon, MapPinIcon } from '@heroicons/react/24/outline';

const refKey = (ref) => `${ref.kind}:${ref.id}`;

function locationLabel(loc, depth) {
  return `${'　'.repeat(depth)}${loc.name}`;
}

function depthOf(loc, byId, seen = new Set()) {
  let depth = 0;
  let current = loc;
  while (current?.parent_id && byId[current.parent_id] && !seen.has(current.parent_id)) {
    seen.add(current.parent_id);
    current = byId[current.parent_id];
    depth += 1;
  }
  return depth;
}

export default function ADServiceSitesEditor({ sites, onChange, locations = [], serviceDcs = [] }) {
  const byId = useMemo(() => {
    const map = {};
    locations.forEach(l => { map[l.id] = l; });
    return map;
  }, [locations]);

  const ordered = useMemo(
    () => locations.map(l => ({ ...l, _depth: depthOf(l, byId) }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [locations, byId]
  );

  const rows = sites || [];

  const addSite = () => onChange([
    ...rows,
    {
      name: '', location_id: null, subnets: [], description: '',
      is_primary: false, pinned_dcs: [],
    },
  ]);

  const update = (idx, patch) => {
    const next = rows.slice();
    next[idx] = { ...next[idx], ...patch };
    // Sólo una sede primaria
    if (patch.is_primary) {
      next.forEach((s, i) => { if (i !== idx) s.is_primary = false; });
    }
    onChange(next);
  };

  const remove = (idx) => {
    const next = rows.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  const allPinned = useMemo(() => {
    const set = new Set();
    rows.forEach(s => (s.pinned_dcs || []).forEach(r => set.add(refKey(r))));
    return set;
  }, [rows]);

  /** DCs que el backend colocaría solo en esta sede (sin fijarlos a mano). */
  const autoDcsFor = (site, idx) => {
    const pinnedHere = new Set((site.pinned_dcs || []).map(refKey));
    return serviceDcs.filter(dc => {
      const key = `${dc.kind}:${dc.id}`;
      if (pinnedHere.has(key)) return false;
      const pinnedElsewhere = rows.some((s, i) =>
        i !== idx && (s.pinned_dcs || []).some(r => refKey(r) === key)
      );
      if (pinnedElsewhere) return false;
      if (site.location_id) return dc.location_id === site.location_id;
      return (dc.site || '') && (dc.site || '') === (site.name || '');
    });
  };

  const dcLabel = (ref) => {
    const dc = serviceDcs.find(d => `${d.kind}:${d.id}` === refKey(ref));
    if (!dc) return `${ref.kind} #${ref.id}`;
    return `${dc.hostname || dc.name} (${ref.kind === 'vm' ? 'VM' : 'físico'})`;
  };

  const pin = (idx, dc) => update(idx, {
    pinned_dcs: [...(rows[idx].pinned_dcs || []), { kind: dc.kind, id: dc.id }],
  });

  const unpin = (idx, ref) => update(idx, {
    pinned_dcs: (rows[idx].pinned_dcs || []).filter(r => refKey(r) !== refKey(ref)),
  });

  const chip = {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    background: 'rgba(59,130,246,.12)', color: 'var(--text-primary)',
    border: '1px solid rgba(59,130,246,.35)', borderRadius: 12,
    padding: '1px 8px', fontSize: '0.72rem',
  };
  const chipAuto = {
    ...chip,
    background: 'var(--bg-input)', border: '1px dashed var(--slate-300)',
    color: 'var(--text-muted)',
  };

  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--slate-300)',
      borderRadius: 8, padding: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1, fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-primary)' }}>
          Sedes documentadas (AD sites)
          <span style={{
            marginLeft: 6, fontSize: '0.7rem', background: 'var(--bg-input)',
            color: 'var(--text-muted)', borderRadius: 4, padding: '0 6px',
          }}>
            {rows.length}
          </span>
        </div>
        <button className="dv-btn" type="button" onClick={addSite}>
          <PlusIcon style={{ width: 14, height: 14 }} /> Añadir sede
        </button>
      </div>

      <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginBottom: 10 }}>
        Renombra las sedes que se crean solas, añade sus <strong>subredes</strong> y marca la
        primaria. Si el «Sitio AD» de un DC no coincide con el nombre de la sede, fíjalo con el
        selector <strong>＋ Fijar un DC a esta sede</strong>.
      </div>

      {rows.length === 0 ? (
        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
          Sin sedes documentadas. Añade una o asigna DCs al servicio y aparecerán automáticamente.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((site, idx) => {
            const autoDcs = autoDcsFor(site, idx);
            const pinned = site.pinned_dcs || [];
            const available = serviceDcs.filter(dc => !allPinned.has(`${dc.kind}:${dc.id}`));
            return (
            <div key={idx} style={{
              border: '1px solid var(--slate-200)', borderRadius: 6, padding: 8,
            }}>
            <div className="row g-2" style={{ alignItems: 'flex-end' }}>
              <div className="col-md-3">
                <label className="form-label" style={{ fontSize: '0.7rem' }}>Nombre de la sede</label>
                <input
                  className="form-control form-control-sm"
                  placeholder="ej. Madrid"
                  value={site.name || ''}
                  onChange={e => update(idx, { name: e.target.value })}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label" style={{ fontSize: '0.7rem' }}>Ubicación (Location)</label>
                <select
                  className="form-select form-select-sm"
                  value={site.location_id ?? ''}
                  onChange={e => update(idx, { location_id: e.target.value === '' ? null : Number(e.target.value) })}
                >
                  <option value="">— Sin vincular —</option>
                  {ordered.map(l => (
                    <option key={l.id} value={l.id}>{locationLabel(l, l._depth)}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label" style={{ fontSize: '0.7rem' }}>Subredes (separadas por comas)</label>
                <input
                  className="form-control form-control-sm"
                  placeholder="ej. 10.10.0.0/24, 10.10.1.0/24"
                  value={Array.isArray(site.subnets) ? site.subnets.join(', ') : (site.subnets || '')}
                  onChange={e => update(idx, {
                    subnets: e.target.value.split(',').map(s => s.trim()).filter(Boolean),
                  })}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label" style={{ fontSize: '0.7rem' }}>Descripción</label>
                <input
                  className="form-control form-control-sm"
                  placeholder="ej. CPD principal"
                  value={site.description || ''}
                  onChange={e => update(idx, { description: e.target.value })}
                />
              </div>
              <div className="col-auto d-flex align-items-center gap-1">
                <button
                  className="dv-btn"
                  type="button"
                  title={site.is_primary ? 'Sede primaria' : 'Marcar como sede primaria'}
                  style={site.is_primary ? { color: '#f59e0b', borderColor: 'rgba(245,158,11,.55)' } : undefined}
                  onClick={() => update(idx, { is_primary: !site.is_primary })}
                >
                  <StarIcon style={{ width: 14, height: 14 }} />
                </button>
                <button className="dv-btn" type="button" title="Eliminar sede" onClick={() => remove(idx)}>
                  <TrashIcon style={{ width: 14, height: 14 }} />
                </button>
              </div>
            </div>

            {/* DCs de la sede */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
              <MapPinIcon style={{ width: 14, height: 14, color: 'var(--text-muted)' }} />
              {pinned.map(ref => (
                <span key={refKey(ref)} style={chip} title="Fijado a esta sede">
                  {dcLabel(ref)}
                  <button
                    type="button"
                    onClick={() => unpin(idx, ref)}
                    title="Quitar el fijado (volverá a agruparse por ubicación / Sitio AD)"
                    style={{
                      border: 'none', background: 'transparent', cursor: 'pointer',
                      color: 'inherit', padding: 0, lineHeight: 1,
                    }}
                  >
                    ✕
                  </button>
                </span>
              ))}
              {autoDcs.map(dc => (
                <span
                  key={`${dc.kind}:${dc.id}`}
                  style={chipAuto}
                  title={`Agrupado automáticamente por ${site.location_id ? 'la ubicación física' : 'el campo «Sitio AD»'}`}
                >
                  {dc.hostname || dc.name}
                </span>
              ))}
              {pinned.length === 0 && autoDcs.length === 0 && (
                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  Sin DCs
                </span>
              )}
              <select
                className="form-select form-select-sm"
                style={{ width: 250 }}
                value=""
                onChange={e => {
                  const dc = serviceDcs.find(d => `${d.kind}:${d.id}` === e.target.value);
                  if (dc) pin(idx, dc);
                }}
              >
                <option value="">＋ Fijar un DC a esta sede…</option>
                {available.map(dc => (
                  <option key={`${dc.kind}:${dc.id}`} value={`${dc.kind}:${dc.id}`}>
                    {dc.hostname || dc.name} ({dc.kind === 'vm' ? 'VM' : 'físico'})
                  </option>
                ))}
              </select>
            </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
